import React, { useEffect, useState } from 'react';
import { getForecast } from '../services/api';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip,
} from 'chart.js';

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Filler, Tooltip);

export default function Forecast({ refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    getForecast()
      .then((r) => setData(r.data))
      .catch(() => setError('Failed to load forecast'))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const fmt = (n) =>
    '$' +
    Number(n)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="skeleton h-8 w-48 rounded" />
        <div className="skeleton h-64 rounded-2xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-6 text-center">
        {error}
      </div>
    );
  }

  if (!data || data.total_predicted === 0) {
    return (
      <div className="bg-white rounded-2xl p-12 shadow-sm border border-slate-100 text-center">
        <div className="text-5xl mb-4">📈</div>
        <h3 className="text-lg font-semibold text-slate-700">No Forecast Available</h3>
        <p className="text-slate-400 mt-2">
          Upload expense transactions to generate a spending forecast.
        </p>
      </div>
    );
  }

  const forecastPoints = data.forecast_data || [];
  const chartData = {
    labels: forecastPoints.map((p) => {
      const d = new Date(p.date);
      return d.toLocaleDateString('default', { month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'Predicted Spending',
        data: forecastPoints.map((p) => p.predicted_amount),
        fill: true,
        borderColor: '#8b5cf6',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        pointBackgroundColor: '#8b5cf6',
        pointBorderWidth: 0,
        pointRadius: 3,
        pointHoverRadius: 6,
        tension: 0.4,
        borderWidth: 2,
      },
    ],
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-800">Expense Forecast</h2>
        <p className="text-sm text-slate-400 mt-1">30-day spending prediction based on your history</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg shadow-purple-500/20 animate-fade-in-up">
          <p className="text-sm font-medium opacity-80">Predicted Monthly Total</p>
          <p className="text-3xl font-bold mt-1">{fmt(data.total_predicted)}</p>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 animate-fade-in-up animate-delay-100">
          <p className="text-sm font-medium text-slate-500">Daily Average</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{fmt(data.daily_average)}</p>
          <p className="text-xs text-slate-400 mt-1">per day</p>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 animate-fade-in-up animate-delay-200">
          <p className="text-sm font-medium text-slate-500">Confidence Range</p>
          <p className="text-lg font-bold text-slate-800 mt-1">
            {fmt(data.confidence_range.lower)} – {fmt(data.confidence_range.upper)}
          </p>
          <p className="text-xs text-slate-400 mt-1">80% confidence interval</p>
        </div>
      </div>

      {/* Chart */}
      {forecastPoints.length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 animate-fade-in-up animate-delay-300">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Daily Forecast</h3>
          <Line
            data={chartData}
            options={{
              responsive: true,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: { label: (ctx) => `$${ctx.raw.toFixed(2)}` },
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: { callback: (v) => `$${v}` },
                  grid: { color: '#f1f5f9' },
                },
                x: { grid: { display: false } },
              },
            }}
          />
        </div>
      )}
    </div>
  );
}
